import React from 'react';
import {
  InfoOutlined as InfoOutlinedIcon,
  KeyboardArrowDownRounded as KeyboardArrowDownRoundedIcon,
} from '@mui/icons-material';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import { guaranteesApplicationStatusStyles } from '../guaranteesApplicationStatusStyles.ts';
import {
  buildStatusFlowInfoAriaLabel,
  STATUS_FLOW_INFO_ICON_SIZE,
  STATUS_FLOW_LOADING_TEXT,
  STATUS_FLOW_TOOLTIP_PLACEMENT,
} from './constants.ts';
import StatusFlowNode from './StatusFlowNode.tsx';
import StatusFlowTooltipContent from './StatusFlowTooltipContent.tsx';
import type { TStatusFlowNode } from './types.ts';
import { getFormattedDateTime, getStatusLabel } from './utils.ts';

interface IStatusFlowContentProps {
  isLoading: boolean;
  nodes: TStatusFlowNode[];
}

const StatusFlowContent: React.FC<IStatusFlowContentProps> = ({ isLoading, nodes }) =>
  isLoading && !nodes.length ? (
    <Box sx={guaranteesClientPageStyles.statusFlowLoading}>
      <CircularProgress size={18} />
      <Typography sx={guaranteesClientPageStyles.statusFlowLoadingText}>
        {STATUS_FLOW_LOADING_TEXT}
      </Typography>
    </Box>
  ) : (
    <Box sx={guaranteesClientPageStyles.statusFlowList}>
      {nodes.map((node, index) => (
        <React.Fragment key={`${node.status}-${index}`}>
          {!!index && (
            <Box sx={guaranteesClientPageStyles.statusFlowTransition}>
              <KeyboardArrowDownRoundedIcon sx={guaranteesClientPageStyles.statusFlowArrow} />
              <Typography sx={guaranteesClientPageStyles.statusFlowDate}>
                {getFormattedDateTime(node.event?.changedAt)}
              </Typography>
              <Tooltip
                title={<StatusFlowTooltipContent event={node.event} />}
                placement={STATUS_FLOW_TOOLTIP_PLACEMENT}
                slotProps={{
                  tooltip: { sx: guaranteesApplicationStatusStyles.tooltip },
                }}
              >
                <Box
                  component="span"
                  tabIndex={0}
                  role="button"
                  aria-label={buildStatusFlowInfoAriaLabel(getStatusLabel(node.status))}
                  sx={guaranteesApplicationStatusStyles.info}
                >
                  <InfoOutlinedIcon sx={{ fontSize: STATUS_FLOW_INFO_ICON_SIZE }} />
                </Box>
              </Tooltip>
            </Box>
          )}
          <StatusFlowNode node={node} />
        </React.Fragment>
      ))}
    </Box>
  );

export default StatusFlowContent;
